<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] p-4 pb-24">
    <!-- LOADING -->
    <Loading v-if="loading"
             :text="$t('views.temperature.loading')" />

    <!-- ERROR -->
    <div v-else-if="error"
         class="text-red-600 text-center">
      {{ error }}
    </div>

    <!-- CONTENT -->
    <div v-else
         class="space-y-6">
      <!-- Chart Module-->
      <ModuleShell :title="$t('views.selectedGoat.modules.temperature')"
                   :icon="Thermometer"
                   hideAction>
        <div v-if="temperatureStore.loading"
             class="text-sm opacity-50 text-center">
          {{ $t('views.selectedGoat.loading') }}
        </div>

        <div class="w-full chart-container">
          <TemperatureChart :temperatures="temperatures" />
        </div>
      </ModuleShell>

      <!-- Temperature List Module -->
      <TemperatureList :temperatures="temperatures"
                       :goatId="goatId"
                       :refreshTemperatures="refreshTemperatures" />
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useTemperatureStore } from '@/stores/temperature.store.js'
import { useGoatStore } from '@/stores/goat.store.js' 
import { useUiStore } from '@/stores/ui.store.js' 
import { storeToRefs } from 'pinia'
import { Thermometer } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

import Loading from '@/components/ui/feedback/Loading.vue'
import BackButton from '@/components/ui/buttons/GetBackButton.vue'
import TemperatureChart from '@/components/goats/charts/TemperatureChart.vue'
import TemperatureList from '@/components/goats/lists/TemperatureList.vue'
import ModuleShell from '@/components/ui/layout/ModuleShell.vue'

const { t } = useI18n()

const temperatureStore = useTemperatureStore()
const goatStore = useGoatStore()
const uiStore = useUiStore()

const route = useRoute()
const goatId = Number(route.params.id)

const { temperatures, loading, error } = storeToRefs(temperatureStore)

const goat = computed(() => goatStore.goats.find((g) => g.id === goatId))

onMounted(async () => {
  if (goatStore.goats.length === 0) {
    await goatStore.fetchGoats()
  }
  await temperatureStore.fetchTemperatures(goatId)
})

// Header 
watch(goat, (g) => {
  uiStore.setHeader({
    title: t('views.selectedGoat.temperature.title'),
    subtitle: g?.eartag || `#${goatId}`,
    showBack: true
  })
}, { immediate: true })

function refreshTemperatures() {
  temperatureStore.fetchTemperatures(goatId)
}
</script>
